import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Network, UserType, UserStatus, Role, Contact } from '../../types';
import { useForm } from 'react-hook-form';
import { X, Plus, UserPlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';
import { ContactSelector } from './ContactSelector';

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  type: UserType;
  status: UserStatus;
  networks?: string[];
  password?: string;
  // Nouveaux champs pour l'association de contact
  contactAssociation?: {
    type: 'none' | 'create' | 'associate';
    contactId?: number;
    contactData?: {
      name: string;
      email: string;
      role: string;
      phone?: string;
      notes?: string;
    };
  };
}

interface UserEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UserFormData) => void;
  user: User | null;
  availableNetworks: Network[];
  errors: Record<string, string>;
}

export const UserEditDialog: React.FC<UserEditDialogProps> = ({
  open,
  onClose,
  onSubmit,
  user,
  availableNetworks,
  errors: serverErrors,
}) => {
  const { t } = useTranslation();
  
  const form = useForm<UserFormData>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: Role.CLIENT,
      networks: [],
      password: '',
      contactAssociation: {
        type: 'none'
      }
    }
  });

  // États pour la gestion des contacts
  const [contactAssociationType, setContactAssociationType] = useState<'none' | 'create' | 'associate'>('none');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    role: '',
    phone: '',
    notes: ''
  });

  // Effet pour remplir le formulaire lors de la modification d'un utilisateur
  useEffect(() => {
    if (user) {
      const nameParts = user.name ? user.name.split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      form.reset({
        email: user.email || '',
        firstName,
        lastName,
        role: user.role,
        networks: user.networks || [],
        password: ''
      });
      setSelectedNetworks(user.networks || []);

      // Gérer le contact existant lors de l'édition
      if (user.contacts && user.contacts.length > 0) {
        const existingContact = user.contacts[0];
        setContactAssociationType('associate');
        setSelectedContact(existingContact);
        setContactFormData({
          name: existingContact.name,
          email: existingContact.email,
          role: existingContact.role || '',
          phone: existingContact.phone || '',
          notes: existingContact.notes || ''
        });
      } else {
        setContactAssociationType('none');
        setSelectedContact(null);
        setContactFormData({
          name: '',
          email: '',
          role: '',
          phone: '',
          notes: ''
        });
      }
    } else {
      form.reset({
        email: '',
        firstName: '',
        lastName: '',
        role: Role.CLIENT,
        networks: [],
        password: ''
      });
      setSelectedNetworks([]);
      setContactAssociationType('none');
      setSelectedContact(null);
      setContactFormData({
        name: '',
        email: '',
        role: '',
        phone: '',
        notes: ''
      });
    }
  }, [user, form]);

  // Effet pour afficher les erreurs serveur dans le formulaire
  useEffect(() => {
    Object.entries(serverErrors).forEach(([key, value]) => {
      form.setError(key as any, {
        type: 'manual',
        message: value
      });
    });
  }, [serverErrors, form]);

  // Gestion des réseaux
  const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);
  const [newNetwork, setNewNetwork] = useState('');

  // Ajouter un réseau
  const addNetwork = () => {
    console.log('🔍 addNetwork called:', { newNetwork, selectedNetworks, condition: newNetwork && !selectedNetworks.includes(newNetwork) });
    if (newNetwork && !selectedNetworks.includes(newNetwork)) {
      const updatedNetworks = [...selectedNetworks, newNetwork];
      setSelectedNetworks(updatedNetworks);
      console.log('🔍 Network added, new selectedNetworks:', updatedNetworks);
      setNewNetwork('');
    }
  };

  // Supprimer un réseau
  const removeNetwork = (network: string) => {
    setSelectedNetworks(selectedNetworks.filter(n => n !== network));
  };

  // Effet quand le rôle change
  const roleValue = form.watch('role');
  useEffect(() => {
    if (roleValue === 'ADMIN') {
      setSelectedNetworks([]);
    }
  }, [roleValue]);

  // Effet pour synchroniser les données de contact avec les données utilisateur
  const formValues = form.watch(['firstName', 'lastName', 'email']);
  useEffect(() => {
    if (contactAssociationType === 'create') {
      const fullName = `${formValues[0] || ''} ${formValues[1] || ''}`.trim();
      setContactFormData(prev => ({
        ...prev,
        name: fullName,
        email: formValues[2] || ''
      }));
    }
  }, [formValues, contactAssociationType]);

  // Soumission du formulaire
  const handleFormSubmit = (data: UserFormData) => {
    console.log('🔍 UserEditDialog submitting:', { ...data, networks: selectedNetworks });
    
    // Gestion des networks
    if (data.role === 'CLIENT') {
      data.networks = selectedNetworks;
    } else {
      data.networks = [];
    }

    // Gestion de l'association de contact
    if (contactAssociationType === 'none') {
      data.contactAssociation = { type: 'none' };
    } else if (contactAssociationType === 'associate' && selectedContact) {
      data.contactAssociation = {
        type: 'associate',
        contactId: selectedContact.id
      };
    } else if (contactAssociationType === 'create') {
      data.contactAssociation = {
        type: 'create',
        contactData: contactFormData
      };
    }

    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="text-2xl">
            {user ? t('userManagement.editUser') : t('userManagement.addUser')}
          </DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-8 px-6">
            <div className="grid gap-4 py-4">
              {/* Informations de base de l'utilisateur */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>{t('userManagement.email')}<span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="email@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('userManagement.firstName')}<span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder={t('userManagement.firstNamePlaceholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('userManagement.lastName')}<span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder={t('userManagement.lastNamePlaceholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!user && (
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>{t('userManagement.password')}<span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>{t('userManagement.role')}<span className="text-red-500">*</span></FormLabel>
                      <Select 
                        onValueChange={(value) => {
                          field.onChange(value);
                          if (value === 'ADMIN') {
                            setSelectedNetworks([]);
                          }
                        }}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('userManagement.selectRole')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="ADMIN">Admin</SelectItem>
                          <SelectItem value="CLIENT">Client</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              {/* Networks - Seulement visible pour les clients */}
              {form.watch('role') === 'CLIENT' && (
                <div className="mt-6">
                  <Label className="text-base font-semibold">{t('userManagement.networks')}</Label>
                  <Separator className="my-2" />
                  
                  <div className="flex flex-wrap gap-2 mt-2 mb-4">
                    {selectedNetworks.map((network) => (
                      <Badge key={network} variant="secondary" className="px-2 py-1">
                        {network}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeNetwork(network)}
                          className="ml-1 h-4 w-4 text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Select 
                        value={newNetwork} 
                        onValueChange={(value) => {
                          console.log('🔍 Network selected:', value);
                          setNewNetwork(value);
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder={t('userManagement.selectNetworkToAdd')} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableNetworks
                            .filter(network => !selectedNetworks.includes(network.name))
                            .map(network => (
                              <SelectItem key={network.id} value={network.name}>{network.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        onClick={() => {
                          console.log('🔍 Add button clicked, newNetwork:', newNetwork);
                          addNetwork();
                        }}
                        disabled={!newNetwork}
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        {t('userManagement.addToUser')}
                      </Button>
                    </div>
                    
                    {availableNetworks.filter(network => !selectedNetworks.includes(network.name)).length === 0 && selectedNetworks.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {t('userManagement.allNetworksAdded')}
                      </p>
                    )}
                  </div>
                  
                  {selectedNetworks.length === 0 && (
                    <p className="text-sm text-amber-500 mt-2">
                      {t('userManagement.networksHelper')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Section Contact Association - pour création et édition */}
            <div className="space-y-4">
              <Separator />
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  {user ? 'Gestion du Contact' : 'Association de Contact'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {user 
                    ? 'Modifier, créer ou supprimer l\'association de contact pour cet utilisateur.'
                    : 'Vous pouvez associer ce nouvel utilisateur à un contact existant ou créer un nouveau contact.'
                  }
                </p>

                {/* Affichage du contact actuel en mode édition */}
                {user && selectedContact && contactAssociationType === 'associate' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-blue-900">{selectedContact.name}</p>
                        <p className="text-sm text-blue-700">{selectedContact.email}</p>
                        {selectedContact.role && (
                          <p className="text-xs text-blue-600">{selectedContact.role}</p>
                        )}
                      </div>
                      <Badge variant="secondary">Contact actuel</Badge>
                    </div>
                  </div>
                )}

                <RadioGroup
                  value={contactAssociationType}
                  onValueChange={(value) => setContactAssociationType(value as 'none' | 'create' | 'associate')}
                  className="grid grid-cols-1 gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="none" id="contact-none" />
                    <Label htmlFor="contact-none">
                      {user ? 'Supprimer l\'association de contact' : 'Aucun contact associé'}
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="associate" id="contact-associate" />
                    <Label htmlFor="contact-associate">
                      {user ? 'Modifier le contact associé' : 'Associer à un contact existant'}
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="create" id="contact-create" />
                    <Label htmlFor="contact-create">Créer un nouveau contact</Label>
                  </div>
                </RadioGroup>

                  {/* Sélecteur de contact existant */}
                  {contactAssociationType === 'associate' && (
                    <div className="mt-4">
                      <ContactSelector
                        selectedContactId={selectedContact?.id}
                        onContactSelect={setSelectedContact}
                        placeholder="Rechercher un contact à associer..."
                      />
                    </div>
                  )}

                  {/* Formulaire de création de contact */}
                  {contactAssociationType === 'create' && (
                    <div className="mt-4 space-y-4 p-4 border rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Nouveau Contact</h4>
                        <span className="text-xs text-muted-foreground">
                          Nom et email synchronisés avec l'utilisateur
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contact-name">Nom *</Label>
                          <Input
                            id="contact-name"
                            value={contactFormData.name}
                            disabled
                            className="bg-muted/50"
                            placeholder="Rempli automatiquement"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-email">Email *</Label>
                          <Input
                            id="contact-email"
                            type="email"
                            value={contactFormData.email}
                            disabled
                            className="bg-muted/50"
                            placeholder="Rempli automatiquement"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-role">Rôle</Label>
                          <Input
                            id="contact-role"
                            value={contactFormData.role}
                            onChange={(e) => setContactFormData({ ...contactFormData, role: e.target.value })}
                            placeholder="Directeur, Manager, etc."
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-phone">Téléphone</Label>
                          <Input
                            id="contact-phone"
                            value={contactFormData.phone}
                            onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
                            placeholder="+33 1 23 45 67 89"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="contact-notes">Notes</Label>
                          <Textarea
                            id="contact-notes"
                            value={contactFormData.notes}
                            onChange={(e) => setContactFormData({ ...contactFormData, notes: e.target.value })}
                            placeholder="Notes additionnelles sur ce contact..."
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            
            <DialogFooter className="px-6 pb-6">
              <Button variant="outline" type="button" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit">
                {user ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
